import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Tooltip } from '../../../components/Tooltip';
import type { Company } from '../../../domain/company';
import { CompanyTabName } from '../../../domain/page';
import { appStore } from '../../../services/appStore';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { CompanyInfoItem } from '../CompanyInfoItem';
import { SectionHeader } from '../SectionHeader';
import { StyledSectionView, StyledSeparator } from '../styles';

import { StyledCompanyInfoItem, StyledCompanyLocaton, SeeMoreButton } from './styles';

export const Firmographics = ({ company }: { company: Company }) => {
  const { t } = useTranslation();

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const handleClickOnEmployees = () => {
    dispatchTrackEventWithLocation(
      'company data',
      'see employees',
      `employee number/${company?.employeeRange || ''}/${company.name}`
    );
    appStore.setCompanyTabName(CompanyTabName.Contacts);
  };

  const onSeeMoreButtonClick = () => {
    const overviewLink = `https://pro.similarweb.com/#/sales/account-overview/${company.domain}/company`;
    window.open(overviewLink, '_blank');

    dispatchTrackEventWithLocation(
      'company data',
      'click see more on firmographics',
      `see more firmographics/${company.domain}`
    );
  };

  const getHeadquarters = (company: Company) => {
    let location = company.country;

    if (company.city) {
      location += `, ${company.city}`;
    }

    const tooltipLocation = company.hqLocationStr ? company.hqLocationStr.trim() : undefined;

    return (
      <StyledCompanyLocaton>
        {location}
        {tooltipLocation ? (
          <Tooltip content={tooltipLocation} minWidth={230} cursor="default">
            <SWReactIcons iconName="location" size="xs" iconColor={colorsPalettes.carbon[200]} />
          </Tooltip>
        ) : null}
      </StyledCompanyLocaton>
    );
  };

  return (
    <StyledSectionView withPadding data-automation="CompanyOverviewFirmographics">
      <SectionHeader title={t('company.firmographics')} withSectionPadding />
      <StyledSeparator />
      <CompanyInfoItem
        iconName="company"
        label={t('company.overview.headquarters')}
        value={getHeadquarters(company)}
        data-automation="CompanyOverviewInfoItem"
      />
      <CompanyInfoItem
        iconName="users"
        label={t('company.overview.employees')}
        value={company.employeeRange}
        onClick={handleClickOnEmployees}
      />
      <StyledCompanyInfoItem iconName="revenue" label={t('company.overview.revenue')} value={company.revenueRange} />
      <SeeMoreButton onClick={onSeeMoreButtonClick} variant="flat" data-automation="firmographics-see-more">
        {t('company.overview.firmographics.see_more')}
        <SWReactIcons iconName="arrow-right" size="xs" iconColor={colorsPalettes.blue[500]} />
      </SeeMoreButton>
    </StyledSectionView>
  );
};
